import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';
import { Link as ScrollLink } from 'react-scroll';
import { Link as RouterLink, useLocation } from 'react-router-dom';

type NavItem = {
  label: string;
  path: string;
  scrollTo: string;
};

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const onHome = location.pathname === '/';

  const menuItems: NavItem[] = [
    { label: t('nav.home'), path: '/', scrollTo: 'hero' },
    { label: t('nav.platform'), path: '/plataforma', scrollTo: 'platform' },
    { label: t('nav.autonomous'), path: '/agentes-autonomos', scrollTo: 'autonomous' },
    { label: t('nav.connectors'), path: '/conectores', scrollTo: 'connectors' },
    { label: t('nav.demo'), path: '/demo', scrollTo: 'demo' },
    { label: t('nav.security'), path: '/seguridad', scrollTo: 'security' },
    { label: t('nav.pricing'), path: '/precios', scrollTo: 'pricing' },
    { label: t('nav.partners'), path: '/partners', scrollTo: 'partners' },
  ];

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'es' : 'en';
    i18n.changeLanguage(newLang);
  };

  const isActive = (item: NavItem) => location.pathname === item.path;

  const renderDesktopLink = (item: NavItem) => {
    // On home page, scroll-link to in-page anchor (no full navigation needed).
    // On other pages, route-link to the dedicated URL.
    if (onHome) {
      return (
        <ScrollLink
          key={item.path}
          to={item.scrollTo}
          smooth
          duration={500}
          offset={-70}
          spy
          activeClass="active"
        >
          <Button
            sx={{
              color: '#475569',
              fontWeight: 600,
              fontSize: '0.92rem',
              textTransform: 'none',
              '&:hover': {
                color: '#0F172A',
                backgroundColor: 'rgba(15, 23, 42, 0.04)',
              },
              '&.active': {
                color: '#0F172A',
                fontWeight: 700,
              },
            }}
          >
            {item.label}
          </Button>
        </ScrollLink>
      );
    }
    return (
      <Button
        key={item.path}
        component={RouterLink}
        to={item.path}
        sx={{
          color: isActive(item) ? '#0F172A' : '#475569',
          fontWeight: isActive(item) ? 700 : 600,
          fontSize: '0.92rem',
          textTransform: 'none',
          '&:hover': {
            color: '#0F172A',
            backgroundColor: 'rgba(15, 23, 42, 0.04)',
          },
        }}
      >
        {item.label}
      </Button>
    );
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'saturate(180%) blur(10px)',
        boxShadow: '0 1px 0 rgba(15, 23, 42, 0.06)',
        zIndex: 1100,
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: '56px', sm: '64px' } }}>
        <RouterLink to="/" style={{ textDecoration: 'none' }} aria-label="WAI - Working AI · Inicio">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src="/logo/wai-final-d1p-serif-filled_navy_white-2048.png"
              alt="WAI - Working AI"
              sx={{
                height: { xs: '32px', sm: '40px' },
                width: 'auto',
                maxWidth: { xs: '120px', sm: '180px' },
              }}
            />
          </Box>
        </RouterLink>

        {!isMobile ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {menuItems.map((item) => renderDesktopLink(item))}

            <IconButton
              onClick={toggleLanguage}
              aria-label={i18n.language === 'en' ? 'Cambiar idioma a Español' : 'Switch language to English'}
              sx={{
                color: '#475569',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                ml: 2,
                '&:hover': {
                  backgroundColor: 'rgba(15, 23, 42, 0.04)',
                  borderColor: '#0F172A',
                  color: '#0F172A',
                },
              }}
            >
              <LanguageIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 0.5, fontSize: '0.75rem', fontWeight: 700 }}>
                {i18n.language === 'en' ? 'ES' : 'EN'}
              </Typography>
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={toggleLanguage}
              aria-label={i18n.language === 'en' ? 'Cambiar idioma a Español' : 'Switch language to English'}
              sx={{
                color: '#475569',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                mr: 1,
                '&:hover': {
                  backgroundColor: 'rgba(15, 23, 42, 0.04)',
                  borderColor: '#0F172A',
                  color: '#0F172A',
                },
              }}
            >
              <LanguageIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 0.5, fontSize: '0.75rem', fontWeight: 700 }}>
                {i18n.language === 'en' ? 'ES' : 'EN'}
              </Typography>
            </IconButton>

            <IconButton
              edge="start"
              color="inherit"
              aria-label="Abrir menú de navegación"
              onClick={handleMenu}
              sx={{ color: '#475569' }}
            >
              <MenuIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} sx={{ mt: 1 }}>
              {menuItems.map((item) => (
                <MenuItem
                  key={item.path}
                  onClick={handleClose}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    fontWeight: isActive(item) ? 700 : 500,
                    color: isActive(item) ? '#0F172A' : '#475569',
                    minWidth: 180,
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
